import React, { useCallback } from 'react';
import {
    IconFlyoutMenuButton,
    IconButtonTheme,
    IconButtonSize,
    Panel,
    PanelTheme,
    FlyoutDirection,
    FlyoutMenuButton,
    ButtonTheme,
    FlyoutMenuTheme,
} from '@apply/components';
import { faBell, faUserCircle, faTrashCan, faEdit } from '@fortawesome/free-regular-svg-icons';
import { FlyoutMenuButtonBorderRadius } from '../FlyoutMenuButtons/FlyoutMenuButton';
import { DemoSection } from './common/SectionNavigator';
import { DemoExample, DemoPage, DemoPageSection } from './common/DemoLayout';

const sections: DemoSection[] = [
    { id: 'flyoutMenuButtonDemo_iconflyoutmenubutton', title: 'Icon Flyout Menu Button' },
    { id: 'flyoutMenuButtonDemo_flyoutmenubutton', title: 'Flyout Menu Button' },
];

export const FlyoutMenuButtonDemo: React.FC = () => {
    const buttonClick: React.MouseEventHandler = useCallback((event) => {
        alert(event.currentTarget.id + ' clicked.');
    }, []);

    return (
        <DemoPage
            title='Flyout Menu Buttons :'
            sections={sections}
        >
            <DemoPageSection
                id={sections[0].id}
                title={sections[0].title}
            >
                <DemoExample>
                    <IconFlyoutMenuButton
                        id='Flyout Left White Bell Button With Count'
                        theme={IconButtonTheme.White}
                        handleClick={buttonClick}
                        size={IconButtonSize.Medium}
                        icon={faBell}
                        bubbleNumber={25}
                        direction={FlyoutDirection.Left}
                    >
                        <Panel
                            theme={PanelTheme.Blue}
                            width='150px'
                        >
                            Panel Items shown left
                        </Panel>
                    </IconFlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <IconFlyoutMenuButton
                        id='Flyout Left White Bell Button With Count'
                        theme={IconButtonTheme.White}
                        handleClick={buttonClick}
                        size={IconButtonSize.Medium}
                        icon={faBell}
                        bubbleNumber={25}
                        direction={FlyoutDirection.Right}
                    >
                        <Panel
                            theme={PanelTheme.Blue}
                            width='150px'
                        >
                            Panel Items shown right
                        </Panel>
                    </IconFlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <IconFlyoutMenuButton
                        id='Fly Down White Profile Button With No Count'
                        theme={IconButtonTheme.White}
                        handleClick={buttonClick}
                        size={IconButtonSize.Medium}
                        icon={faUserCircle}
                        hint='View Profile'
                        direction={FlyoutDirection.Down}
                    >
                        <ul style={{ textAlign: 'center' }}>
                            <li>
                                <a>Logout</a>
                            </li>
                        </ul>
                    </IconFlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <IconFlyoutMenuButton
                        id='White Profile Button Disabled'
                        theme={IconButtonTheme.White}
                        handleClick={buttonClick}
                        size={IconButtonSize.Medium}
                        icon={faUserCircle}
                        disabled={true}
                        direction={FlyoutDirection.Down}
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </IconFlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <IconFlyoutMenuButton
                        id='Flyout Left Transparent Bell Button With Count'
                        theme={IconButtonTheme.Transparent}
                        handleClick={buttonClick}
                        size={IconButtonSize.Medium}
                        icon={faBell}
                        bubbleNumber={25}
                        direction={FlyoutDirection.Left}
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </IconFlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <IconFlyoutMenuButton
                        id='Flyout Right Transparent Bell Button With Count'
                        theme={IconButtonTheme.Transparent}
                        handleClick={buttonClick}
                        size={IconButtonSize.Medium}
                        icon={faBell}
                        bubbleNumber={25}
                        direction={FlyoutDirection.Right}
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </IconFlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <IconFlyoutMenuButton
                        id='Flyout Down Transparent Profile Button With No Count'
                        theme={IconButtonTheme.Transparent}
                        handleClick={buttonClick}
                        size={IconButtonSize.Medium}
                        icon={faUserCircle}
                        hint='View Profile'
                        direction={FlyoutDirection.Down}
                    >
                        <ul style={{ textAlign: 'center' }}>
                            <li>
                                <a>Logout</a>
                            </li>
                        </ul>
                    </IconFlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <IconFlyoutMenuButton
                        id='Transparent Profile Button Disabled'
                        theme={IconButtonTheme.Transparent}
                        handleClick={buttonClick}
                        size={IconButtonSize.Medium}
                        icon={faUserCircle}
                        disabled={true}
                        direction={FlyoutDirection.Down}
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </IconFlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <Panel
                        theme={PanelTheme.White}
                        width='40px'
                    >
                        <IconFlyoutMenuButton
                            id='Red Trash Button With Count'
                            theme={IconButtonTheme.Red}
                            handleClick={buttonClick}
                            size={IconButtonSize.Small}
                            icon={faTrashCan}
                            bubbleNumber={9}
                            direction={FlyoutDirection.Left}
                        >
                            <ul>
                                <li>Item 1</li>
                            </ul>
                        </IconFlyoutMenuButton>
                        <IconFlyoutMenuButton
                            id='Red Trash Button With Count'
                            theme={IconButtonTheme.Red}
                            handleClick={buttonClick}
                            size={IconButtonSize.Small}
                            icon={faTrashCan}
                            bubbleNumber={9}
                            direction={FlyoutDirection.Right}
                        >
                            <ul>
                                <li>Item 1</li>
                            </ul>
                        </IconFlyoutMenuButton>
                        <IconFlyoutMenuButton
                            id='Red Trash Button With No Count'
                            theme={IconButtonTheme.Red}
                            handleClick={buttonClick}
                            size={IconButtonSize.Small}
                            icon={faTrashCan}
                            hint='Delete Item'
                            direction={FlyoutDirection.Down}
                        >
                            <ul style={{ textAlign: 'center' }}>
                                <li>
                                    <a>View Details</a>
                                </li>
                            </ul>
                        </IconFlyoutMenuButton>
                        <IconFlyoutMenuButton
                            id='Disabled Red Trash Button With No Count'
                            theme={IconButtonTheme.Red}
                            handleClick={buttonClick}
                            size={IconButtonSize.Small}
                            icon={faTrashCan}
                            hint='Delete Item'
                            disabled={true}
                            direction={FlyoutDirection.Down}
                        >
                            <ul>
                                <li>Item 1</li>
                            </ul>
                        </IconFlyoutMenuButton>
                        <IconFlyoutMenuButton
                            id='Blue Edit Button With Count'
                            theme={IconButtonTheme.Blue}
                            handleClick={buttonClick}
                            size={IconButtonSize.Small}
                            icon={faEdit}
                            bubbleNumber={3}
                            direction={FlyoutDirection.Left}
                        >
                            <ul>
                                <li>Item 1</li>
                            </ul>
                        </IconFlyoutMenuButton>
                        <IconFlyoutMenuButton
                            id='Blue Edit Button With Count'
                            theme={IconButtonTheme.Blue}
                            handleClick={buttonClick}
                            size={IconButtonSize.Small}
                            icon={faEdit}
                            bubbleNumber={3}
                            direction={FlyoutDirection.Right}
                        >
                            <ul>
                                <li>Item 1</li>
                            </ul>
                        </IconFlyoutMenuButton>
                        <IconFlyoutMenuButton
                            id='Blue Edit Button With No Count'
                            theme={IconButtonTheme.Blue}
                            handleClick={buttonClick}
                            size={IconButtonSize.Small}
                            icon={faEdit}
                            direction={FlyoutDirection.Down}
                        >
                            <ul style={{ textAlign: 'center' }}>
                                <li>
                                    <a>Logout</a>
                                </li>
                            </ul>
                        </IconFlyoutMenuButton>
                        <IconFlyoutMenuButton
                            id='Disabled Blue Edit Button With No Count'
                            theme={IconButtonTheme.Blue}
                            handleClick={buttonClick}
                            size={IconButtonSize.Small}
                            icon={faEdit}
                            disabled={true}
                            direction={FlyoutDirection.Down}
                        >
                            <ul>
                                <li>Item 1</li>
                            </ul>
                        </IconFlyoutMenuButton>
                    </Panel>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection
                id={sections[1].id}
                title={sections[1].title}
            >
                <DemoExample>
                    <FlyoutMenuButton
                        id='Flyout Button (dark blue active)'
                        theme={FlyoutMenuTheme.DarkBlueActive}
                        borderRadius={FlyoutMenuButtonBorderRadius.Small}
                        handleClick={buttonClick}
                        disabled={false}
                        direction={FlyoutDirection.Down}
                        buttonContent='Flyout Button'
                        width='150px'
                        height='40px'
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </FlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <FlyoutMenuButton
                        id='Flyout Button (blue)'
                        theme={FlyoutMenuTheme.Blue}
                        borderRadius={FlyoutMenuButtonBorderRadius.Small}
                        handleClick={buttonClick}
                        disabled={false}
                        direction={FlyoutDirection.Down}
                        buttonContent='Flyout Button'
                        width='150px'
                        height='40px'
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </FlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <FlyoutMenuButton
                        id='Flyout Button (red)'
                        theme={FlyoutMenuTheme.Red}
                        borderRadius={FlyoutMenuButtonBorderRadius.Small}
                        handleClick={buttonClick}
                        disabled={false}
                        direction={FlyoutDirection.Down}
                        buttonContent='Flyout Button'
                        width='150px'
                        height='40px'
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </FlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <FlyoutMenuButton
                        id='Flyout Button (transparent)'
                        theme={FlyoutMenuTheme.Transparent}
                        borderRadius={FlyoutMenuButtonBorderRadius.Small}
                        handleClick={buttonClick}
                        disabled={false}
                        direction={FlyoutDirection.Down}
                        buttonContent='Flyout Button'
                        width='150px'
                        height='40px'
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </FlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <FlyoutMenuButton
                        id='Flyout Button (transparent red)'
                        theme={FlyoutMenuTheme.TransparentRed}
                        borderRadius={FlyoutMenuButtonBorderRadius.Small}
                        handleClick={buttonClick}
                        disabled={false}
                        direction={FlyoutDirection.Down}
                        buttonContent='Flyout Button'
                        width='150px'
                        height='40px'
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </FlyoutMenuButton>
                </DemoExample>
                <DemoExample>
                    <FlyoutMenuButton
                        id='Flyout Button (white)'
                        theme={FlyoutMenuTheme.White}
                        borderRadius={FlyoutMenuButtonBorderRadius.Small}
                        handleClick={buttonClick}
                        disabled={false}
                        direction={FlyoutDirection.Right}
                        buttonContent='Flyout Button'
                        width='150px'
                        height='40px'
                    >
                        <ul>
                            <li>Item 1</li>
                        </ul>
                    </FlyoutMenuButton>
                </DemoExample>
            </DemoPageSection>
        </DemoPage>
    );
};
